import { useUnitName } from "../UnitNames";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Dialog } from "@akin2unde/flowui-react";
import type { Sale } from "../../models/Sale";
import type { InventoryWareHouse } from "../../models/InventoryWareHouse";
import { InventoryWareHouseService } from "../../services/InventoryWareHouseService";
import { useTenant } from "../../tenant/useTenant";
import { useAuth } from "../../auth/useAuth";
import { resolveImageUrl } from "../../utils/ImageUrl";
import { formatDateTime } from "../../utils/DateTime";
import { apiClient } from "../../api/ApiClient";
import "./Receipt.css";
export default function ReceiptPreview({
  sale,
  onClose,
}: {
  sale: Sale;
  onClose: () => void;
}) {
  const unitName=useUnitName();
  const { Tenant } = useTenant();
  const { state } = useAuth();
  const [width, setWidth] = useState<58 | 80>(80);
  const [warehouse, setWarehouse] = useState<InventoryWareHouse>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [customer, setCustomer] = useState(sale.CustomerCode || "Walk-in");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setWarehouse(undefined);
    setLoading(true);
    setError("");
    if (!sale.WareHouse) {
      setError(
        "This sale has no warehouse recorded. Assign its warehouse before printing.",
      );
      setLoading(false);
      return;
    }
    void InventoryWareHouseService()
      .GetByCode(sale.WareHouse, c.signal)
      .then((x) => {
        if (!c.signal.aborted) setWarehouse(x);
      })
      .catch(() => {
        if (!c.signal.aborted)
          setError(
            "Could not load the sale's warehouse contact details. Retry before printing.",
          );
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    if (sale.CustomerCode)
      void apiClient
        .get<{ BusinessName: string }>(
          `customer/SearchWithCode/${encodeURIComponent(sale.CustomerCode)}`,
          { signal: c.signal, showBusyIndicator: false, showError: false },
        )
        .then((x) => {
          if (!c.signal.aborted)
            setCustomer(x.data.BusinessName || sale.CustomerCode!);
        })
        .catch(() => {});
    return () => c.abort();
  }, [sale.Code, sale.WareHouse, retry]);
  const money = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: sale.CurrencyCode || "NGN",
    maximumFractionDigits: 2,
  });
  const subtotal = (sale.Items ?? []).reduce(
    (sum, x) => sum + x.Quantity * x.UnitPrice,
    0,
  );
  const discount=Math.max(sale.DiscountAmount||0,(sale.Items??[]).reduce((s,l)=>s+Number(l.DiscountAmount||0),0));
  const logo = resolveImageUrl(Tenant?.LogoUrl);
  const receipt = (
    <article className={`thermal-receipt receipt-${width}`}>
      <header>
        {logo && (
          <img
            src={logo}
            alt={`${Tenant?.BusinessName} logo`}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <h2>{Tenant?.BusinessName || "Sales receipt"}</h2>
        <p>Sales receipt</p>
        <p>{warehouse?.Address}</p>
        <p>{warehouse?.PhoneNumber}</p>
      </header>
      <div className="receipt-meta">
        <p>
          <span>Receipt</span>
          <strong>{sale.SaleNumber || sale.Code}</strong>
        </p>
        <p>
          <span>Date</span>
          <span>{formatDateTime(sale.SoldAt)}</span>
        </p>
        <p>
          <span>Warehouse</span>
          <span>{warehouse?.Name || sale.WareHouse}</span>
        </p>
        <p>
          <span>Printed by</span>
          <span>{state.User?.Name}</span>
        </p>
        <p>
          <span>Customer</span>
          <span>{customer}</span>
        </p>
      </div>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(sale.Items ?? []).map((x, i) => (
            <tr key={i}>
              <td>
                {x.ProductName || x.ProductCode}{x.UnitCode ? ` (${unitName(x.UnitCode)})` : ""}
                <small>{money.format(x.UnitPrice)} each</small>
              </td>
              <td>{x.Quantity}</td>
              <td>{money.format(x.Quantity * x.UnitPrice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="receipt-totals">
        <p>
          <span>Subtotal</span>
          <span>{money.format(subtotal)}</span>
        </p>
        <p>
          <span>Discount</span>
          <span>{money.format(discount)}</span>
        </p>
        <p className="receipt-grand-total">
          <strong>TOTAL</strong>
          <strong>{money.format(sale.TotalAmount)}</strong>
        </p>
        <p>
          <span>Payment</span>
          <span>
            {sale.PaymentType} · {sale.PaymentStatus}
          </span>
        </p>
        <p>
          <span>Amount paid</span>
          <span>{money.format(sale.AmountPaid)}</span>
        </p>
        {sale.AmountPaid > sale.TotalAmount ? (
          <p>
            <span>Change</span>
            <span>{money.format(sale.AmountPaid - sale.TotalAmount)}</span>
          </p>
        ) : (
          <p>
            <span>Balance due</span>
            <span>
              {money.format(
                sale.BalanceDue ??
                  Math.max(0, sale.TotalAmount - sale.AmountPaid),
              )}
            </span>
          </p>
        )}
        {sale.PaymentDueAt && sale.BalanceDue > 0 && (
          <p>
            <span>Payment due</span>
            <span>
              {new Date(sale.PaymentDueAt).toLocaleDateString("en-NG")}
            </span>
          </p>
        )}
      </div>
      <footer>
        <strong>Thank you for shopping with us!</strong>
        <p>Please keep this receipt.</p>
      </footer>
    </article>
  );
  const print = async () => {
    if (loading || error || !warehouse || printing) return;
    setPrinting(true);
    try {
      await document.fonts.ready;
      await Promise.all(
        Array.from(
          document.querySelectorAll<HTMLImageElement>(
            ".receipt-print-root img",
          ),
        ).map((x) => x.decode().catch(() => {})),
      );
      window.print();
    } finally {
      setPrinting(false);
    }
  };
  return (
    <>
      <Dialog
        open
        header="Receipt preview"
        width="600px"
        maxWidth="full"
        onClose={onClose}
        closeOnOverlayClick
        closeOnEscape
        showCloseButton
        body={
          <div className="receipt-preview">
            <div className="receipt-paper-options">
              <span>Paper size</span>
              {([80, 58] as const).map((x) => (
                <Button
                  key={x}
                  variant={width === x ? "solid" : "outline"}
                  onClick={() => setWidth(x)}
                >
                  {x} mm
                </Button>
              ))}
            </div>
            {loading && <p role="status">Loading warehouse details…</p>}
            {error && (
              <div role="alert">
                <p>{error}</p>
                <Button
                  variant="outline"
                  onClick={() => setRetry((x) => x + 1)}
                >
                  Retry
                </Button>
              </div>
            )}
            {!loading && !error && receipt}
            <small>
              Choose the matching paper size in your printer settings. Only the
              receipt will print.
            </small>
          </div>
        }
        footer={
          <div className="receipt-preview-actions">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              disabled={loading || !!error || printing}
              onClick={() => void print()}
            >
              <i className="fa-solid fa-print" /> Print receipt
            </Button>
          </div>
        }
      />
      {!loading &&
        !error &&
        warehouse &&
        createPortal(
          <div className="receipt-print-root">{receipt}</div>,
          document.body,
        )}
    </>
  );
}
